import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getProfileSchemaForRole,
  type ProfileFormValues,
  toProfileFormValues,
  adminProfileSchema,
} from '../validation/profile.schemas';
import { usersApi } from '../api/users.api';
import { filesApi } from '@/core/api/files.api';
import { FILE_FOLDERS } from '@/constants/file-types';
import { toastOneSuccess, toastOneError } from '@/toast';
import { getErrorMessage } from '@/core/api/response-handler';
import { applyServerFieldErrors } from '@/shared/utils';

/**
 * useProfileWithForm - Form hook for user profile editing
 *
 * Features:
 * - Role-specific validation (PROFESOR, ELEV, ADMIN)
 * - File upload for protocol documents (PROFESOR only)
 * - Change detection (enable/disable save button)
 * - Client-side validation with Zod
 * - Backend error mapping
 *
 * Context7 patterns:
 * - Dynamic schema based on user role
 * - watch() for change detection
 * - setError() for backend error mapping
 * - Direct API submission (no mapping functions!)
 *
 * @param role - User role (PROFESOR, ELEV, ADMIN)
 *
 * @example
 * const { form, onSubmit, loading, isDirty, handleFileUpload, removeFile } =
 *   useProfileWithForm(userInfo.role);
 */
export function useProfileWithForm(role: string) {
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Get role-specific schema
  const schema = getProfileSchemaForRole(role);

  // Fetch profile data
  const {
    data: profileData,
    isLoading: isLoadingProfile,
    refetch,
  } = useQuery({
    queryKey: ['profile'],
    queryFn: () => usersApi.getProfile(),
  });

  // Schema se alege la runtime, dupa rol, deci nu se poate sti static care e activa.
  // Un singur cast, aici, la granita — in loc de `useForm<any>`, care sterge verificarea
  // pe tot formularul. Numele de campuri raman verificate in componenta.
  const form = useForm<ProfileFormValues>({
    // zodResolver are nevoie de un ZodObject concret (citeste `_def.typeName`), iar
    // `schema` e o uniune de trei. Ii dam unul dintre ele ca forma, si tipam rezultatul:
    // schema care valideaza efectiv ramane cea aleasa la runtime.
    resolver: zodResolver(
      schema as typeof adminProfileSchema
    ) as unknown as Resolver<ProfileFormValues>,
    defaultValues: toProfileFormValues(profileData),
    mode: 'onBlur',
  });

  // Update form when profile data loads
  useEffect(() => {
    if (profileData) {
      form.reset(toProfileFormValues(profileData));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  // Detect if form has unsaved changes using react-hook-form's built-in isDirty
  // This is more reliable than manual JSON comparison
  // IMPORTANT: Destructure to enable React Hook Form Proxy subscription
  const { isDirty } = form.formState;

  // Handle file upload for protocolDeColaborare (PROFESOR only)
  const handleFileUpload = async (file: File) => {
    if (!file) return;

    setIsUploadingFile(true);
    try {
      // filesApi.uploadFile now returns string URL directly
      const documentUrl = await filesApi.uploadFile(file, FILE_FOLDERS.USER_PROTOCOLS);

      // Get current documents array
      const currentDocs = form.getValues('protocolDeColaborare') || [];

      // Append new document URL
      form.setValue('protocolDeColaborare', [...currentDocs, documentUrl], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      });
    } catch (error: unknown) {
      console.error('File upload error:', error);
      const message = getErrorMessage(error, '');
      if (message) {
        toastOneError(message);
      }
    } finally {
      setIsUploadingFile(false);
    }
  };

  // Remove file from protocolDeColaborare array
  const removeFile = (index: number) => {
    const currentDocs = form.getValues('protocolDeColaborare') || [];
    const updatedDocs = currentDocs.filter((_: string, i: number) => i !== index);
    form.setValue('protocolDeColaborare', updatedDocs, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };

  // Replace all protocol files (clears existing array for new upload)
  const replaceAllProtocols = () => {
    form.setValue('protocolDeColaborare', [], {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
  };

  // Update profile mutation
  const mutation = useMutation({
    mutationFn: async (data: ProfileFormValues) => {
      console.log('[DEBUG] Mutation function executing with:', data);
      // Direct submission - no mapping needed!
      return usersApi.updateProfile(data);
    },
    onSuccess: async () => {
      // Reset form to clear isDirty flag before any navigation
      // Valorile curente devin noile defaultValues, deci isDirty devine false.
      // NU folosi `reset(undefined, { keepValues: true })`: cu valori omise,
      // react-hook-form pastreaza defaultValues-urile INITIALE si recalculeaza
      // isDirty prin _getDirty() -> ramane true dupa o salvare reusita, iar
      // navigarea de mai jos loveste blocker-ul din useFormNavigation
      // (dialogul "Modificari nesalvate" dupa succes, defect T7/D5).
      form.reset(form.getValues());

      toastOneSuccess('Profilul a fost actualizat cu succes!');

      // Refetch profile data to sync with backend
      const result = await refetch();

      // Context7 Pattern: Reset form with fresh data
      if (result.data) {
        form.reset(toProfileFormValues(result.data));
      }
    },
    onError: (error) => {
      // Context7 Pattern: Map backend errors to form fields
      applyServerFieldErrors(error, form.setError);

      // Show generic error message
      if (error.message) {
        toastOneError(error.message);
      } else {
        toastOneError('A apărut o eroare la actualizarea profilului');
      }
    },
  });

  // Submit handler
  //
  // NU re-arunca eroarea: `onError` de mai sus a aratat deja toastul si erorile pe campuri, iar
  // singurul apelant (`ProfilePage`, din dialogul de confirmare) nu asteapta promisiunea. Un
  // `throw` aici nu ajungea la nimeni — devenea `unhandledrejection`, raportat de telemetrie ca
  // `PromiseRejectionEvent: ApiValidationError` (07.10). Cine are nevoie de rezultat il citeste
  // din valoarea intoarsa: `true` = salvat, `false` = respins.
  const onSubmit = async (data: ProfileFormValues): Promise<boolean> => {
    console.log('[DEBUG] onSubmit called with data:', data);
    try {
      await mutation.mutateAsync(data);
      console.log('[DEBUG] Mutation completed successfully');
      return true;
    } catch (error) {
      console.log('[DEBUG] Mutation failed:', error);
      return false;
    }
  };

  return {
    form,
    onSubmit,
    loading: isLoadingProfile || mutation.isPending || isUploadingFile,
    isSubmitting: mutation.isPending,
    isUploadingFile,
    isDirty, // Using formState.isDirty instead of manual JSON comparison
    hasChanged: isDirty, // Keep hasChanged for backward compatibility
    handleFileUpload,
    removeFile,
    replaceAllProtocols,
    profileData,
  };
}
